import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import Layout from './components/Layout/Layout';
import AdminLayout from './components/Layout/AdminLayout';
import Login from './pages/Auth/Login';
import Register from './pages/Auth/Register';
import AdminLoginPage from './pages/Auth/AdminLoginPage';
import EnhancedFinancialDashboard from './pages/Dashboard/EnhancedFinancialDashboard';
import Properties from './pages/Properties/Properties';
import Units from './pages/Units/Units';
import UnitsForRent from './pages/UnitsForRent/UnitsForRent';
import Payments from './pages/Payments/Payments';
import Analytics from './pages/Analytics/Analytics';
import OwnerViewings from './pages/Owner/OwnerViewings';
import Utilities from './pages/Utilities/Utilities';
import PropertyQR from './pages/PropertyQR/PropertyQR';
import Communications from './pages/Communications/Communications';
import Reports from './pages/Reports/Reports';
import ModernAdminDashboard from './pages/Admin/ModernAdminDashboard';
import AdminPropertyOwners from './pages/Admin/AdminPropertyOwners';
import AdminProperties from './pages/Admin/AdminProperties';
import AdminUnits from './pages/Admin/AdminUnits';
import AdminInspections from './pages/Admin/AdminInspections';
import AdminAgents from './pages/Admin/AdminAgents';
import AdminAirbnb from './pages/Admin/AdminAirbnb';
import AdminListingRequests from './pages/Admin/AdminListingRequests';
import AdminPaymentIntents from './pages/Admin/AdminPaymentIntents';
import AdminListingReports from './pages/Admin/AdminListingReports';
import AdminMaintenance from './pages/Admin/AdminMaintenance';
import AdminTenants from './pages/Admin/AdminTenants';
import AdminSystemHealth from './pages/Admin/AdminSystemHealth';
import AdminActivityLogs from './pages/Admin/AdminActivityLogs';
import AdminNotifications from './pages/Admin/AdminNotifications';
import AdminAnalytics from './pages/Admin/AdminAnalytics';
import AdminRevenue from './pages/Admin/AdminRevenue';
import AdminPaymentMethods from './pages/Admin/AdminPaymentMethods';
import AdminViewingPayments from './pages/Admin/AdminViewingPayments';
import AdminSettings from './pages/Admin/AdminSettings';
import AdditionalServices from './pages/Admin/AdditionalServices';
import InspectionPayment from './pages/InspectionPayment/InspectionPayment';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import AuthGuard from './components/Auth/AuthGuard';
import AgentLogin from './pages/Agent/AgentLogin';
import AgentLayout from './components/Layout/AgentLayout';
import AgentDashboard from './pages/Agent/AgentDashboard';
import AgentMyUnits from './pages/Agent/AgentMyUnits';
import AgentAddUnit from './pages/Agent/AgentAddUnit';
import AgentInspections from './pages/Agent/AgentInspections';
import AgentProfile from './pages/Agent/AgentProfile';
import AgentUnitDetails from './pages/Agent/AgentUnitDetails';
import AgentProtectedRoute from './components/Auth/AgentProtectedRoute';
import PublicRentals from './pages/Public/PublicRentals';
import RentalUnitDetails from './pages/Public/RentalUnitDetails';
import PublicAirbnb from './pages/Public/PublicAirbnb';
import AirbnbDetails from './pages/Public/AirbnbDetails';
import AirbnbPayment from './pages/Public/AirbnbPayment';
import Guidelines from './pages/Public/Guidelines';
import Portals from './pages/Public/Portals';
import TenantRentPaymentReturn from './pages/Public/TenantRentPaymentReturn';
import OwnerAirbnb from './pages/Airbnb/OwnerAirbnb';
import OwnerPropertyHub from './pages/Owner/OwnerPropertyHub';
import OwnerBilling from './pages/Owner/OwnerBilling';
import OwnerViewingPayments from './pages/Owner/OwnerViewingPayments';
import OwnerLogin from './pages/Auth/OwnerLogin';
import OwnerRegister from './pages/Auth/OwnerRegister';

function App() {
  return (
    <Routes>
      {/* Home Page - Shows Rentals */}
      <Route path="/" element={<PublicRentals />} />
      
      {/* Public Routes */}
      <Route path="/rentals" element={<PublicRentals />} />
      <Route path="/rental/:id" element={<RentalUnitDetails />} />
      <Route path="/airbnb" element={<PublicAirbnb />} />
      <Route path="/airbnb/payment/:bookingId" element={<AirbnbPayment />} />
      <Route path="/airbnb/:id" element={<AirbnbDetails />} />
      <Route path="/guidelines" element={<Guidelines />} />
      <Route path="/portals" element={<Portals />} />
      <Route path="/tenant/rent-payment-return" element={<TenantRentPaymentReturn />} />
      
      {/* Client Auth Routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      
      {/* Professional Auth Routes (Hidden from main public UI) */}
      <Route path="/admin-login" element={<AdminLoginPage />} />
      <Route path="/agent-login" element={<AgentLogin />} />
      <Route path="/owner-login" element={<OwnerLogin />} />
      <Route path="/owner-register" element={<OwnerRegister />} />
      
      <Route path="/inspection-payment/:paymentId" element={<InspectionPayment />} />
      
      {/* Property Owner Routes */}
      <Route path="/owner" element={
        <AuthGuard>
          <ProtectedRoute requiredRoles={['owner', 'admin']}>
            <Layout />
          </ProtectedRoute>
        </AuthGuard>
      }>
        <Route index element={<Navigate to="/owner/dashboard" replace />} />
        <Route path="dashboard" element={<EnhancedFinancialDashboard />} />
        <Route path="property-hub" element={<OwnerPropertyHub />} />
        <Route path="properties" element={<Navigate to="/owner/property-hub?tab=properties" replace />} />
        <Route path="units" element={<Navigate to="/owner/property-hub?tab=units" replace />} />
        <Route path="units-for-rent" element={<Navigate to="/owner/property-hub?tab=units-for-rent" replace />} />
        <Route path="airbnb" element={<Navigate to="/owner/property-hub?tab=airbnb" replace />} />
        <Route path="tenants" element={<Navigate to="/owner/property-hub?tab=tenants" replace />} />
        <Route path="payments" element={<Navigate to="/owner/property-hub?tab=payments" replace />} />
        <Route path="billing" element={<OwnerBilling />} />
        <Route path="utilities" element={<Utilities />} />
        <Route path="inspections" element={<OwnerViewings />} />
        <Route path="viewings" element={<OwnerViewings />} />
        <Route path="viewing-payments" element={<OwnerViewingPayments />} />
        <Route path="property-qr" element={<PropertyQR />} />
        <Route path="communications" element={<Communications />} />
        <Route path="reports" element={<Reports />} />
        <Route path="analytics" element={<Analytics />} />
      </Route>
      
      {/* Admin Routes - Separate Layout */}
      <Route path="/admin" element={
        <AuthGuard>
          <ProtectedRoute requiredRoles={['admin']}>
            <AdminLayout />
          </ProtectedRoute>
        </AuthGuard>
      }>
        <Route index element={<ModernAdminDashboard />} />
        <Route path="owners" element={<AdminPropertyOwners />} />
        <Route path="properties" element={<AdminProperties />} />
        <Route path="internal-units" element={<Units />} />
        <Route path="units" element={<AdminUnits />} />
        <Route path="listing-requests" element={<AdminListingRequests />} />
        <Route path="listing-reports" element={<AdminListingReports />} />
        <Route path="payment-intents" element={<AdminPaymentIntents />} />
        <Route path="maintenance" element={<AdminMaintenance />} />
        <Route path="inspections" element={<AdminInspections />} />
        <Route path="additional-services" element={<AdditionalServices />} />
        <Route path="payment-methods" element={<AdminPaymentMethods />} />
        <Route path="viewing-payments" element={<AdminViewingPayments />} />
        <Route path="agents" element={<AdminAgents />} />
        <Route path="airbnb" element={<AdminAirbnb />} />
        <Route path="tenants" element={<AdminTenants />} />
        <Route path="system" element={<AdminSystemHealth />} />
        <Route path="activity" element={<AdminActivityLogs />} />
        <Route path="notifications" element={<AdminNotifications />} />
        <Route path="settings" element={<AdminSettings />} />
        <Route path="analytics" element={<AdminAnalytics />} />
        <Route path="revenue" element={<AdminRevenue />} />
        <Route path="communications" element={<Communications />} />
        <Route path="reports" element={<Reports />} />
      </Route>
      
      {/* Agent Routes - Mobile-Optimized Layout */}
      <Route path="/agent" element={
        <AgentProtectedRoute>
          <AgentLayout />
        </AgentProtectedRoute>
      }>
        <Route index element={<AgentDashboard />} />
        <Route path="my-units" element={<AgentMyUnits />} />
        <Route path="unit/:id" element={<AgentUnitDetails />} />
        <Route path="add-unit" element={<AgentAddUnit />} />
        <Route path="inspections" element={<AgentInspections />} />
        <Route path="profile" element={<AgentProfile />} />
      </Route>
      
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

export default App;
